import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import DatasetSelector from '../../Common/DatasetSelector';
import DiscretizationLayout from '../../Common/DiscretizationLayout';

const BarChart = ({ plotData, defaultTitle, xlabel, ylabel, titleKey }) => {
  const plotRef = useRef(null);
  const [title, setTitle] = useState(() => localStorage.getItem(`lra_title_${titleKey}`) || defaultTitle || '');
  const [xLabel, setXLabel] = useState(() => localStorage.getItem(`lra_xlabel_${titleKey}`) || xlabel || '');
  const [yLabel, setYLabel] = useState(() => localStorage.getItem(`lra_ylabel_${titleKey}`) || ylabel || '');
  const [showEditor, setShowEditor] = useState(false);

  const renderPlot = (t, xl, yl) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    const x = plotData.map(d => d.grade);
    const y = plotData.map(d => d.lra_mean);
    window.Plotly.newPlot(plotRef.current, [{
      x, y, type: 'bar', marker: { color: '#3498db' }
    }], {
      title: t, xaxis: { title: xl, tickangle: -45, automargin: true }, yaxis: { title: yl },
      template: 'plotly_white', height: 400,
      margin: { t: 40, b: 120, l: 60, r: 20 }
    }, { responsive: true });
  };

  useEffect(() => { if (plotData && plotData.length > 0) renderPlot(title, xLabel, yLabel); }, [plotData, title, xLabel, yLabel]);

  const applyTitles = () => {
    renderPlot(title, xLabel, yLabel);
    localStorage.setItem(`lra_title_${titleKey}`, title);
    localStorage.setItem(`lra_xlabel_${titleKey}`, xLabel);
    localStorage.setItem(`lra_ylabel_${titleKey}`, yLabel);
    setShowEditor(false);
  };

  if (!plotData || plotData.length === 0) return <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>No data available. Run analysis first.</div>;

  return (
    <div style={{ marginBottom: '20px', background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>{title}</h4>
        <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '2px 8px', fontSize: '11px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>✏️ Edit</button>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', gap: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} style={{ flex: 2, fontSize: '12px', padding: '4px' }} />
          <input placeholder="X axis" value={xLabel} onChange={e => setXLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
          <input placeholder="Y axis" value={yLabel} onChange={e => setYLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
          <button onClick={applyTitles} style={{ background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '400px' }}></div>
    </div>
  );
};

const LRA = () => {
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetLoaded, setDatasetLoaded] = useState(false);
  const [saveColName, setSaveColName] = useState('LRA_LGD');
  const [bootstrapIterations, setBootstrapIterations] = useState(500);
  const [confidenceLevel, setConfidenceLevel] = useState(75);

  const [groups, setGroups] = useState({
    segmentation: false,
    target: false,
    bootstrap: false
  });

  const [formData, setFormData] = useState({
    Default_Date: null,
    Calibration_segment: null,
    Grade: null,
    Target_variable: null
  });

  const columnOptions = columns.map(c => ({ value: c, label: c }));

  useEffect(() => {
    if (!window.Plotly) {
      const script = document.createElement('script');
      script.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js';
      document.head.appendChild(script);
    }
  }, []);

  const hdl = (data) => {
    setColumns(data.columns || []);
    setDatasetInfo(data);
    setDatasetLoaded(true);
    toast.success(`Loaded ${data.rows?.toLocaleString()} rows`);
  };

  const hfc = (f, v) => setFormData(p => ({ ...p, [f]: v }));

  const toggleGroup = (group) => {
    setGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const saveParams = async () => {
    if (!formData.Default_Date || !formData.Target_variable) {
      toast.error('Please select Default Date and Target Variable');
      return;
    }
    if (!formData.Grade && !formData.Calibration_segment) {
      toast.error('Please select either Grade or Calibration segment (or both)');
      return;
    }
    const payload = {
      Default_Date: formData.Default_Date.value,
      Calibration_segment: formData.Calibration_segment?.value || '',
      Grade: formData.Grade?.value || '',
      Target_variable: formData.Target_variable.value,
      n_bootstrap: bootstrapIterations,
      Quantile_MoC: confidenceLevel
    };
    setLoading(true);
    try {
      await axios.post('/api/lra/step0', payload);
      toast.success('Parameters saved');
      setStep(1);
    } catch (err) {
      toast.error('Failed to save parameters');
    } finally { setLoading(false); }
  };

  const runAnalysis = async () => {
    const payload = {
      Default_Date: formData.Default_Date.value,
      Calibration_segment: formData.Calibration_segment?.value || '',
      Grade: formData.Grade?.value || '',
      Target_variable: formData.Target_variable.value,
      n_bootstrap: bootstrapIterations,
      Quantile_MoC: confidenceLevel
    };
    setLoading(true);
    try {
      const response = await axios.post('/api/lra/step1', payload);
      setResults(response.data.results);
      toast.success(response.data.message);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally { setLoading(false); }
  };

  const [loadingStep, setLoadingStep] = useState(null);

  const saveLRA = async () => {
    if (!saveColName.trim()) { toast.error('Enter a column name'); return; }
    setLoadingStep('save');
    try {
      const response = await axios.post('/api/lra/save-lra', { column_name: saveColName.trim() });
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save LRA');
    } finally { setLoadingStep(null); }
  };

  const exportResults = async () => {
    const customTitles = {
      title: localStorage.getItem('lra_title_lra') || '',
      xlabel: localStorage.getItem('lra_xlabel_lra') || 'Grade',
      ylabel: localStorage.getItem('lra_ylabel_lra') || 'LGD'
    };
    try {
      const response = await axios.post('/api/lra/export-with-titles', customTitles, { responseType: 'blob' }).catch(() => axios.get('/api/lra/export', { responseType: 'blob' }));
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lra_results.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Failed to export results'); }
  };

  const sc = (t) => {
    if (t === 0) setStep(0);
    else if (t === 1 && datasetLoaded && formData.Default_Date) setStep(1);
    else if (t === 2 && results) setStep(2);
  };

  const cs = {
    control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px', borderColor: '#e0e0e0' }),
    valueContainer: (b) => ({ ...b, padding: '0 8px' }),
    placeholder: (b) => ({ ...b, fontSize: '13px', color: '#95a5a6' }),
    menu: (b) => ({ ...b, zIndex: 9999, position: 'absolute' }),
    menuPortal: (b) => ({ ...b, zIndex: 9999 })
  };

  const groupStyle = {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    marginBottom: '16px',
    overflow: 'visible',
    position: 'relative'
  };

  const groupHeaderStyle = {
    background: '#f5f7fa',
    padding: '12px 16px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '14px',
    color: '#2c3e50'
  };

  const groupContentStyle = {
    padding: '16px',
    background: 'white',
    overflow: 'visible'
  };

  return (
    <DiscretizationLayout title="LRA - Loss Rate Analysis" step={step} onStepClick={sc} datasetLoaded={datasetLoaded} results={results}>
      {step === 0 && (
        <>
          <DatasetSelector apiPrefix="/api/lra" onDatasetLoaded={hdl} />
          {columns.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              {/* 1. Segmentation */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('segmentation')}>
                  <span>🎯 1. Segmentation</span>
                  <span>{groups.segmentation ? '−' : '+'}</span>
                </div>
                {groups.segmentation && (
                  <div style={groupContentStyle}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Calibration segment (optional)</label>
                        <Select options={columnOptions} value={formData.Calibration_segment} onChange={v => hfc('Calibration_segment', v)} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select calibration segment..." />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Grade (optional)</label>
                        <Select options={columnOptions} value={formData.Grade} onChange={v => hfc('Grade', v)} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select grade column..." />
                      </div>
                    </div>
                    <p style={{ fontSize: '11px', color: '#e74c3c', marginTop: '8px' }}>⚠️ At least one of Grade or Calibration segment must be selected</p>
                  </div>
                )}
              </div>

              {/* 2. Target variable */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('target')}>
                  <span>📊 2. Target variable and Date</span>
                  <span>{groups.target ? '−' : '+'}</span>
                </div>
                {groups.target && (
                  <div style={groupContentStyle}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Default Date *</label>
                        <Select options={columnOptions} value={formData.Default_Date} onChange={v => hfc('Default_Date', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select default date column..." />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Target Variable *</label>
                        <Select options={columnOptions} value={formData.Target_variable} onChange={v => hfc('Target_variable', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select target variable..." />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 3. Bootstrap settings */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('bootstrap')}>
                  <span>🎲 3. Bootstrap Settings</span>
                  <span>{groups.bootstrap ? '−' : '+'}</span>
                </div>
                {groups.bootstrap && (
                  <div style={groupContentStyle}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Bootstrap Iterations</label>
                        <input type="number" value={bootstrapIterations} onChange={e => setBootstrapIterations(parseInt(e.target.value))} style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #e0e0e0', borderRadius: '4px' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Confidence Level (%)</label>
                        <input type="number" value={confidenceLevel} onChange={e => setConfidenceLevel(parseInt(e.target.value))} style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #e0e0e0', borderRadius: '4px' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button onClick={saveParams} disabled={loading} style={{ padding: '8px 28px', background: loading ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>{loading ? 'Saving...' : 'Continue →'}</button>
              </div>
            </div>
          )}
        </>
      )}

      {step === 1 && (
        <div>
          <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Review Parameters</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '8px', fontSize: '13px' }}>
              <p><strong>Default Date:</strong> {formData.Default_Date?.label}</p>
              <p><strong>Target Variable:</strong> {formData.Target_variable?.label}</p>
              <p><strong>Grade:</strong> {formData.Grade?.label || "(not provided)"}</p>
              <p><strong>Calibration segment:</strong> {formData.Calibration_segment?.label || "(not provided)"}</p>
              <p><strong>Bootstrap Iterations:</strong> {bootstrapIterations}</p>
              <p><strong>Confidence Level:</strong> {confidenceLevel}%</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => setStep(0)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={runAnalysis} disabled={loading} style={{ padding: '8px 24px', background: loading ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer' }}>{loading ? 'Running...' : 'Run Analysis →'}</button>
          </div>
        </div>
      )}

      {step === 2 && results && (
        <div>
          <BarChart plotData={results.bar_data} defaultTitle="LRA LGD by Grade" xlabel="Grade" ylabel="LGD" titleKey="lra" />

          <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Column Name</label>
                <input type="text" value={saveColName} onChange={e => setSaveColName(e.target.value)} style={{ padding: '6px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', width: '150px' }} />
              </div>
              <button onClick={saveLRA} disabled={loadingStep === 'save'} style={{ padding: '8px 20px', background: loadingStep === 'save' ? '#95a5a6' : '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: loadingStep === 'save' ? 'not-allowed' : 'pointer', fontSize: '13px', whiteSpace: 'nowrap' }}>{loadingStep === 'save' ? 'Saving...' : '💾 Save to Dataset'}</button>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setStep(1)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
              <button onClick={exportResults} style={{ padding: '8px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>📥 Export</button>
            </div>
          </div>
        </div>
      )}
    </DiscretizationLayout>
  );
};

export default LRA;
